<script>
import _ from 'lodash'
import { createHelpers } from 'vuex-map-fields'
import { mapState } from 'vuex'
import Pagination from '@/components/UI/Pagination.vue'
import Profile from './Profile.vue'

const { mapFields } = createHelpers({
  getterType: 'marketplace/getField',
  mutationType: 'marketplace/updateField',
});

export default {
  name: 'Marketplace',

  data() {
    return {
      isSearchBarEmpty: true,
      isWaitlistUser: false,
      loading: false,
      loadingProfiles: false,
      showSendMessageDialog: false,
    }
  },

  components: {
    Pagination,
    Profile,
  },

  computed: {
    ...mapState('auth', ['user']),
    ...mapState('shop', ['currentShop']),
    ...mapState('marketplace', ['profiles']),
    ...mapFields([
      'categories',
      'countries',
      'currentPage',
      'messageString',
      'productsRange',
      'profile',
      'profilesPagination',
      'queries',
      'selectedProfile',
      'showCreateProfileDialog',
      'showMessageDialog',
      'showMessageSentDialog',
    ]),
  },

  async created() {
    try {
      this.loading = true
      await this.$store.dispatch(`shop/init`)
      await this.$store.dispatch('marketplace/fetchProfile', this.currentShop.id)
      let waitlist = this.currentShop && this.currentShop.coco_waiting_list && this.currentShop.coco_waiting_list !== undefined
      waitlist === undefined ? this.isWaitlistUser = false : this.isWaitlistUser = true
      if(this.isWaitlistUser) {
        await this.fetchProfilesHandler()
      }
    } catch(error) {
      throw new Error(error)
    } finally {
      this.loading = false
    }

    this.debouncedSearch = _.debounce(this.searchProfilesHandler, 500);
  },

  watch: {
    async 'queries.search_str'(val, previousVal) {
      if(val.length > 2 || val.length < previousVal.length) {
        this.currentPage = 1
        await this.debouncedSearch()
        this.isSearchBarEmpty = false
      }
    },

    'currentShop'(val) {
      let waitlist = val && val.coco_waiting_list && val.coco_waiting_list !== undefined
      waitlist === undefined ? this.isWaitlistUser = false : this.isWaitlistUser = true
    }
  },

  methods: {
    hideMessageDialogHandler() {
      this.selectedProfile = null
      this.showMessageDialog = false
      this.showMessageSentDialog = false
    },

    async fetchProfilesHandler() {
      this.loadingProfiles = true
      await this.$store.dispatch('marketplace/fetchProfiles')
      setTimeout(() => {
        this.loadingProfiles = false
      }, 250)
    },

    async searchProfilesHandler() {
      this.loadingProfiles = true
      this.updateCurrentPageHandler(1, false)
      await this.$store.dispatch('marketplace/fetchProfiles')
      setTimeout(() => {
        this.loadingProfiles = false
      }, 250)
    },

    async sendMessageHandler() {
      const response = await this.$store.dispatch('marketplace/sendMessage', {
        message: this.messageString,
        storeId: this.currentShop.id,
        target_store_id: this.selectedProfile.store_id
      })
      this.messageString = null
      this.showMessageDialog = false
      this.showMessageSentDialog = true
    },

    async updateCurrentPageHandler(page, fetch = true) {
      this.currentPage = page
      if(fetch) {
        await this.fetchProfilesHandler()
      }
    },

    goToReviewProfileHandler() {
      this.showCreateProfileDialog = false
      this.$router.push({ name: 'MarketplaceSettings' })
    }
  }
}
</script>

<template>
  <article v-if="loading">
    <v-row class="mt-9" style="width: 1267px; margin-left: auto; margin-right: auto;">
      <v-col v-for="item in 8" :key="item" cols="3" class="marketplace-profile-loader m-t-4">
        <div class="loader-image p-loading"></div>
        <div class="loader-bottom">
          <div class="first mb-4 p-loading"></div>
          <div class="second mb-4 p-loading"></div>
          <div class="third p-loading"></div>
        </div>
      </v-col>
    </v-row>
  </article>
  <article v-else class="marketplace v2" style="width: 1267px; margin: 0 auto;">
    <template v-if="isWaitlistUser">
      <div class="search-section d-flex flex-column align-center m-t-2 m-b-4">
        <h3 class="m-b-4">Find your perfect match</h3>

        <v-text-field
          append-icon="search"
          background-color="#fff"
          dense
          label="Search for category or store name"
          :loading="loadingProfiles"
          outlined
          style="width: 665px;"
          v-model="queries.search_str"
        />
      </div>

      <div class="d-flex align-center justify-between m-b-3 p-b-3" style="border-bottom: solid 1px #DDD;">
        <h4 class="m-0">
          <span v-if="queries.search_str.length === 0">
            All
            <span v-if="currentShop && currentShop.type === 'destination'">Source</span>
            <span v-else-if="currentShop && currentShop.type === 'source'">Destination</span>
            stores
          </span>
          <span v-else>Search results for "{{ queries.search_str }}"</span>
        </h4>

        <router-link to="/settings/marketplace-settings" exact tag="div" class="d-flex align-center pointer" style="font-weight: 600">
          <svg width="24" height="25" viewBox="0 0 25 26" fill="none" xmlns="http://www.w3.org/2000/svg" class="mr-1">
            <path d="M12.4221 16.0314C11.8335 16.0314 11.2581 15.8568 10.7687 15.5298C10.2793 15.2028 9.89783 14.738 9.67258 14.1942C9.44733 13.6504 9.3884 13.0521 9.50323 12.4748C9.61806 11.8975 9.9015 11.3672 10.3177 10.951C10.7339 10.5348 11.2642 10.2514 11.8415 10.1365C12.4188 10.0217 13.0171 10.0806 13.5609 10.3059C14.1047 10.5311 14.5695 10.9126 14.8965 11.402C15.2235 11.8914 15.3981 12.4668 15.3981 13.0554C15.3981 13.8446 15.0845 14.6016 14.5264 15.1597C13.9683 15.7178 13.2113 16.0314 12.4221 16.0314ZM12.4221 11.6029C12.1348 11.6029 11.854 11.6881 11.6151 11.8477C11.3763 12.0073 11.1901 12.2341 11.0802 12.4995C10.9702 12.7649 10.9415 13.057 10.9975 13.3387C11.0536 13.6205 11.1919 13.8793 11.395 14.0824C11.5981 14.2855 11.8569 14.4239 12.1387 14.4799C12.4204 14.5359 12.7125 14.5072 12.9779 14.3973C13.2433 14.2873 13.4701 14.1012 13.6297 13.8623C13.7893 13.6234 13.8745 13.3426 13.8745 13.0554C13.8745 12.6701 13.7215 12.3007 13.4491 12.0283C13.1767 11.7559 12.8073 11.6029 12.4221 11.6029V11.6029Z" fill="#0E3B4D"/>
            <path d="M12.4829 21.9428C12.1859 21.9415 11.8921 21.8815 11.6184 21.7663C11.3446 21.6511 11.0964 21.483 10.8878 21.2716C10.6792 21.0602 10.5145 20.8097 10.403 20.5344C10.2915 20.2591 10.2355 19.9646 10.2382 19.6676C10.2342 19.576 10.2044 19.4874 10.1523 19.4119C10.1002 19.3365 10.0279 19.2772 9.94361 19.241C9.8504 19.1982 9.74659 19.1841 9.64533 19.2004C9.54407 19.2166 9.44993 19.2626 9.37482 19.3324C8.95311 19.7509 8.38443 19.9879 7.79032 19.9926C7.19246 19.9965 6.6167 19.7669 6.18551 19.3527C5.97606 19.1433 5.80991 18.8947 5.69655 18.6211C5.58319 18.3475 5.52485 18.0542 5.52485 17.7581C5.52485 17.4619 5.58319 17.1687 5.69655 16.895C5.80991 16.6214 5.97606 16.3728 6.18551 16.1634C6.25714 16.0981 6.30859 16.0137 6.33379 15.9201C6.35898 15.8265 6.35687 15.7277 6.32771 15.6353C6.27821 15.5495 6.20721 15.4782 6.1217 15.4283C6.0362 15.3784 5.93915 15.3517 5.84017 15.3509C5.24255 15.3485 4.6692 15.1142 4.24097 14.6974C3.81274 14.2805 3.5631 13.7137 3.54468 13.1163C3.54601 12.8202 3.60565 12.5273 3.7202 12.2542C3.83475 11.9811 4.00196 11.7333 4.21229 11.5249C4.42261 11.3164 4.67194 11.1514 4.94602 11.0394C5.22011 10.9273 5.51358 10.8703 5.8097 10.8716C5.90131 10.8676 5.98993 10.8378 6.06539 10.7857C6.14085 10.7336 6.20008 10.6613 6.23629 10.5771C6.27907 10.4838 6.29322 10.38 6.27695 10.2788C6.26068 10.1775 6.21471 10.0834 6.14488 10.0083C5.72637 9.58656 5.48942 9.01787 5.48467 8.42377C5.47948 8.12719 5.53338 7.83254 5.64325 7.55702C5.75311 7.28149 5.91673 7.03059 6.12456 6.81896C6.33396 6.6095 6.58256 6.44335 6.85617 6.33C7.12979 6.21664 7.42305 6.15829 7.71922 6.15829C8.01539 6.15829 8.30865 6.21664 8.58227 6.33C8.85588 6.44335 9.10448 6.6095 9.31387 6.81896C9.38701 6.88853 9.47679 6.93815 9.57461 6.96305C9.67243 6.98795 9.77501 6.9873 9.87251 6.96115C9.96139 6.91248 10.0367 6.8424 10.0917 6.75727C10.1467 6.67215 10.1796 6.57465 10.1874 6.47362C10.1871 5.87424 10.4202 5.29831 10.8374 4.86791C11.2545 4.4375 11.8228 4.18645 12.4219 4.16797C12.718 4.17062 13.0107 4.23158 13.2833 4.34736C13.5559 4.46314 13.8029 4.63147 14.0104 4.84274C14.2179 5.05401 14.3818 5.30409 14.4926 5.57869C14.6035 5.85328 14.6592 6.14703 14.6565 6.44314C14.6603 6.53608 14.6887 6.62636 14.7386 6.70483C14.7885 6.78331 14.8583 6.8472 14.9409 6.89005C15.0305 6.92297 15.1276 6.92995 15.221 6.91018C15.3145 6.89042 15.4004 6.84472 15.469 6.77833C15.8907 6.35982 16.4594 6.12286 17.0535 6.11812C17.3506 6.10881 17.6465 6.16082 17.9226 6.27092C18.1987 6.38101 18.4492 6.54683 18.6583 6.75801C18.8678 6.9674 19.0339 7.21601 19.1473 7.48962C19.2607 7.76324 19.319 8.0565 19.319 8.35267C19.319 8.64883 19.2607 8.9421 19.1473 9.21571C19.0339 9.48933 18.8678 9.73793 18.6583 9.94732C18.594 10.0234 18.55 10.1145 18.5305 10.2122C18.5109 10.3099 18.5165 10.411 18.5466 10.506C18.5953 10.5948 18.6654 10.6702 18.7505 10.7252C18.8356 10.7801 18.9331 10.813 19.0342 10.8208C19.6282 10.8284 20.1965 11.065 20.6204 11.4813C21.0442 11.8976 21.2911 12.4615 21.3093 13.0554C21.3067 13.3515 21.2457 13.6442 21.1299 13.9167C21.0142 14.1893 20.8458 14.4364 20.6346 14.6439C20.4233 14.8514 20.1732 15.0152 19.8986 15.1261C19.624 15.2369 19.3303 15.2926 19.0342 15.2899C18.9412 15.2938 18.8509 15.3221 18.7725 15.372C18.694 15.422 18.6301 15.4918 18.5872 15.5743V15.5743C18.5543 15.664 18.5474 15.7611 18.5671 15.8545C18.5869 15.9479 18.6326 16.0339 18.699 16.1025C19.1175 16.5242 19.3544 17.0929 19.3592 17.687C19.3644 17.9836 19.3105 18.2782 19.2006 18.5537C19.0907 18.8293 18.9271 19.0802 18.7193 19.2918C18.5099 19.5012 18.2613 19.6674 17.9877 19.7808C17.7141 19.8941 17.4208 19.9525 17.1246 19.9525C16.8285 19.9525 16.5352 19.8941 16.2616 19.7808C15.988 19.6674 15.7394 19.5012 15.53 19.2918C15.4614 19.2254 15.3754 19.1797 15.282 19.1599C15.1886 19.1402 15.0915 19.1472 15.0018 19.1801C14.9161 19.2296 14.8448 19.3006 14.7949 19.3861C14.745 19.4716 14.7183 19.5686 14.7174 19.6676C14.7098 20.2617 14.4732 20.8299 14.0569 21.2538C13.6406 21.6777 13.0767 21.9245 12.4829 21.9428ZM9.73031 17.6667C9.99971 17.6635 10.2666 17.719 10.5124 17.8292C10.8752 17.9804 11.1863 18.2336 11.4081 18.5581C11.6298 18.8826 11.7527 19.2645 11.7617 19.6574C11.7589 19.8534 11.8327 20.0426 11.9674 20.1849C12.1021 20.3272 12.2871 20.4113 12.4829 20.4192C12.6786 20.4037 12.8611 20.3143 12.9932 20.169C13.1253 20.0237 13.1971 19.8335 13.1939 19.6371C13.1963 19.2428 13.3101 18.8572 13.5221 18.5247C13.7342 18.1923 14.0359 17.9265 14.3924 17.7581C14.7633 17.6038 15.1716 17.5629 15.5657 17.6407C15.9599 17.7184 16.3221 17.9113 16.6066 18.1948C16.7413 18.3295 16.924 18.4052 17.1145 18.4052C17.305 18.4052 17.4876 18.3295 17.6223 18.1948C17.757 18.0601 17.8327 17.8775 17.8327 17.687C17.8327 17.4965 17.757 17.3138 17.6223 17.1791C17.3405 16.8935 17.1495 16.5307 17.0736 16.1366C16.9977 15.7426 17.0402 15.3348 17.1957 14.9649C17.3559 14.6085 17.6156 14.3058 17.9434 14.0933C18.2713 13.8807 18.6536 13.7672 19.0443 13.7664C19.2375 13.7692 19.4242 13.6964 19.5646 13.5636C19.705 13.4308 19.788 13.2485 19.7959 13.0554C19.7829 12.8587 19.6941 12.6748 19.5483 12.5422C19.4024 12.4096 19.2109 12.3387 19.0138 12.3444C18.6162 12.3336 18.2296 12.2118 17.8976 11.9928C17.5655 11.7739 17.3013 11.4664 17.1348 11.1052C16.9828 10.7306 16.9431 10.3198 17.0207 9.92297C17.0983 9.52616 17.2897 9.16052 17.5715 8.87068C17.6382 8.80398 17.6911 8.72481 17.7272 8.63767C17.7633 8.55053 17.7819 8.45714 17.7819 8.36282C17.7819 8.26851 17.7633 8.17511 17.7272 8.08798C17.6911 8.00084 17.6382 7.92166 17.5715 7.85497C17.5049 7.78828 17.4257 7.73538 17.3385 7.69928C17.2514 7.66319 17.158 7.64461 17.0637 7.64461C16.9694 7.64461 16.876 7.66319 16.7888 7.69928C16.7017 7.73538 16.6225 7.78828 16.5558 7.85497C16.2703 8.1202 15.9144 8.29769 15.5308 8.36625C15.1471 8.43482 14.7518 8.39157 14.3921 8.24167C14.0323 8.09178 13.7233 7.84155 13.5018 7.52085C13.2804 7.20015 13.1558 6.82252 13.1431 6.43299C13.1459 6.23976 13.0731 6.05306 12.9403 5.91267C12.8075 5.77228 12.6252 5.68929 12.4321 5.68137C12.2354 5.69442 12.0515 5.7832 11.9189 5.92904C11.7863 6.07489 11.7154 6.26644 11.7211 6.46346C11.7103 6.86105 11.5885 7.2477 11.3695 7.57974C11.1506 7.91178 10.8431 8.17599 10.4819 8.34251C10.1073 8.49452 9.69648 8.53417 9.29967 8.45661C8.90287 8.37905 8.53723 8.18765 8.24739 7.90576C8.18069 7.83907 8.10152 7.78616 8.01438 7.75007C7.92724 7.71398 7.83385 7.6954 7.73953 7.6954C7.64522 7.6954 7.55182 7.71398 7.46469 7.75007C7.37755 7.78616 7.29837 7.83907 7.23168 7.90576C7.16499 7.97245 7.11209 8.05162 7.07599 8.13876C7.0399 8.2259 7.02132 8.31929 7.02132 8.41361C7.02132 8.50793 7.0399 8.60132 7.07599 8.68846C7.11209 8.77559 7.16499 8.85477 7.23168 8.92146C7.51185 9.20825 7.7009 9.57153 7.775 9.96555C7.84911 10.3596 7.80496 10.7667 7.64812 11.1357C7.4969 11.4985 7.24369 11.8096 6.91918 12.0314C6.59466 12.2531 6.21279 12.376 5.81985 12.385C5.62393 12.3822 5.43466 12.456 5.29236 12.5907C5.15007 12.7254 5.066 12.9104 5.05808 13.1062C5.07355 13.3019 5.16305 13.4844 5.30835 13.6165C5.45365 13.7486 5.64383 13.8203 5.84017 13.8172C6.23449 13.8196 6.6201 13.9334 6.95255 14.1454C7.28501 14.3575 7.55078 14.6591 7.71922 15.0157C7.87351 15.3866 7.91437 15.7949 7.83663 16.189C7.75889 16.5832 7.56604 16.9454 7.28247 17.2299C7.21577 17.2966 7.16287 17.3758 7.12678 17.4629C7.09068 17.5501 7.07211 17.6435 7.07211 17.7378C7.07211 17.8321 7.09068 17.9255 7.12678 18.0126C7.16287 18.0998 7.21577 18.1789 7.28247 18.2456C7.34916 18.3123 7.42833 18.3652 7.51547 18.4013C7.60261 18.4374 7.696 18.456 7.79032 18.456C7.88464 18.456 7.97803 18.4374 8.06517 18.4013C8.1523 18.3652 8.23148 18.3123 8.29817 18.2456C8.68423 17.877 9.19656 17.6699 9.73031 17.6667V17.6667Z" fill="#212121"/>
          </svg>
          My profile settings
        </router-link>
      </div>

      <v-row class="filters">
        <v-col cols="3">
          <v-autocomplete
            :items="countries"
            :menu-props="{ 'content-class': 'select-menu' }"
            @change="searchProfilesHandler"
            background-color="#fff"
            clearable
            dense
            hide-details
            item-text="name"
            item-value="country"
            label="Location"
            outlined
            v-model="queries['filters[country_name]']"
          />
        </v-col>
        <v-col cols="3">
          <v-select
            :items="productsRange"
            :menu-props="{ 'content-class': 'select-menu' }"
            @change="searchProfilesHandler"
            background-color="#fff"
            clearable
            dense
            hide-details
            label="Number of products"
            outlined
            v-model="queries['filters[product_count]']"
          />
        </v-col>
        <v-col cols="3">
          <v-select
            :items="categories"
            :menu-props="{ 'content-class': 'select-menu' }"
            @change="searchProfilesHandler"
            background-color="#fff"
            clearable
            dense
            hide-details
            label="Category"
            outlined
            v-model="queries['filters[category]']"
          />
        </v-col>
        <v-col cols="3" class="d-flex justify-end">
          <Pagination :pagination="profilesPagination" @updateCurrentPage="updateCurrentPageHandler" />
        </v-col>
      </v-row>

      <v-row class="m-y-2">
        <Profile v-for="_profile in profiles" :key="_profile.id" :profile="_profile" :messageAllowed="profile.updated_at !== null" />
      </v-row>

      <v-overlay :value="showMessageDialog" class="dialog-inner message-dialog" v-if="selectedProfile">
        <v-card light width="506px">
          <div class="dialog-body">
            <div class="text-center">
              <svg width="120" height="99" viewBox="0 0 120 99" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_501_32004)">
                  <path d="M73.088 88.0385C70.2368 90.8925 67.7793 93.3526 66.0862 95.0475C61.6723 99.4656 57.0375 96.8883 55.2719 95.0475L27.1135 67.9248C22.9564 64.0608 19.4428 60.2462 3.73003 45.2605C2.12164 43.6505 -0.427039 39.5388 2.24536 35.9719C4.91776 32.4052 19.9376 13.6794 27.1135 4.76233C29.2902 1.60987 34.0205 -2.14076 40.0541 3.4676C45.2505 8.29766 63.0664 25.0171 78.2843 37.7288" stroke="#0E3B4D" stroke-width="1.00791" stroke-linecap="round"/>
                  <path d="M26.8157 5.40674C27.702 21.6625 33.6824 47.8307 64.6129 57.6794" stroke="#0E3B4D" stroke-width="1.00791" stroke-linecap="round"/>
                  <path d="M106.3 54.0712C105.98 55.9471 105.654 56.7363 105.397 59.0706C105.061 62.1115 104.84 65.3007 104.896 68.0111C104.994 72.769 105.946 76.0519 108.63 74.4681C114.016 71.2892 116.923 68.6827 117.693 66.0244C118.988 61.5541 118.994 52.9944 111.737 47.3983C102.976 40.6421 93.1001 40.8254 85.3253 47.3983C77.6101 53.921 75.5003 64.3651 75.9228 69.7831C76.3852 75.7097 80.8293 88.4384 95.3301 88.6373C109.831 88.8359 117.688 81.0569 118.896 77.4144" stroke="#0E3B4D" stroke-width="1.00791" stroke-linecap="round"/>
                  <path d="M105.397 59.0703C103.976 55.4777 93.0689 51.2954 88.9504 59.0703C84.7027 67.0895 87.9477 73.8451 90.7097 75.1696C94.2372 76.8612 99.4226 77.746 104.896 68.0108" stroke="#0E3B4D" stroke-width="1.00791" stroke-linecap="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_501_32004">
                    <rect width="119" height="98.0684" fill="white" transform="translate(0.5)"/>
                  </clipPath>
                </defs>
              </svg>

              <h3 class="m-t-1">Send a message to {{ selectedProfile.brand_name }}</h3>

              <ol start="1" class="m-y-3 text-left">
                <li>Add your message below. Include relevant information like why you want to work together and your preferred contact method.</li>
                <li>We'll send your message and store details via email and CC you</li>
                <li>Get to know each other and agree to terms</li>
              </ol>

              <textarea v-model="messageString" placeholder="Type your message..." class="message-box"></textarea>
            </div>

            <div class="actions d-flex justify-end">
              <v-btn elevation="0" class="btn btn-border btn-message m-r-1" @click="hideMessageDialogHandler">Cancel</v-btn>
              <v-btn :disabled="!messageString" elevation="0" class="btn btn-border inverse btn-message" @click="sendMessageHandler">Send</v-btn>
            </div>
          </div>
        </v-card>
      </v-overlay>

      <v-overlay :value="showMessageSentDialog" class="dialog-inner message-dialog">
        <v-card light width="506px">
          <div class="dialog-body">
            <div class="text-center">
              <svg width="120" height="82" viewBox="0 0 120 82" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clip-path="url(#clip0_529_32998)">
                  <path d="M66.3562 4.62402C75.9195 11.5373 75.3338 40.6305 73.8457 54.3129H77.0142C81.623 25.5077 76.0541 10.4331 72.6934 6.49636L66.3562 4.62402Z" fill="#0E3B4D"/>
                  <path d="M100.058 14.4177C109.604 20.3491 109.019 45.3102 107.534 57.0493H110.697C115.297 32.3353 109.738 19.4017 106.384 16.0241L100.058 14.4177Z" fill="#0E3B4D"/>
                  <path d="M118.205 55.3209C119.838 42.4067 119.358 19.1704 106.107 15.7139" stroke="#0E3B4D" stroke-width="0.801887" stroke-linecap="round"/>
                  <path d="M106.107 15.7143L52.3857 0.735596" stroke="#0E3B4D" stroke-width="0.801887" stroke-linecap="round"/>
                  <path d="M118.919 57.472L71.7115 54.4678C71.6191 54.4621 71.541 54.5354 71.541 54.6279V59.6346C71.541 59.7196 71.6073 59.7898 71.6921 59.7948L118.9 62.5127C118.992 62.518 119.069 62.4449 119.069 62.3526V57.6321C119.069 57.5474 119.004 57.4773 118.919 57.472Z" stroke="#0E3B4D" stroke-width="0.801887" stroke-linecap="round"/>
                  <path d="M85.3672 60.7944V80.67" stroke="#0E3B4D" stroke-width="0.801887" stroke-linecap="round"/>
                  <path d="M75.8611 60.3621V74.0445" stroke="#0E3B4D" stroke-width="0.801887" stroke-linecap="round"/>
                  <path d="M3.99514 67.8309C10.3238 72.2939 51.8615 60.7265 71.2531 54.6011L31.6461 56.1854C18.3804 57.8261 -2.33349 63.3681 3.99514 67.8309Z" fill="#0E3B4D" stroke="#0E3B4D" stroke-width="0.801887" stroke-linecap="round"/>
                  <path d="M71.5406 59.354C60.4178 65.1784 31.4983 77.0063 5.28882 74.587" stroke="#0E3B4D" stroke-width="0.801887" stroke-linecap="round"/>
                  <path d="M5.28903 74.4763L2.98462 66.843" stroke="#0E3B4D" stroke-width="0.801887" stroke-linecap="round"/>
                  <path d="M65.9239 53.449C68.1323 34.3417 67.5081 -2.95139 47.3445 0.735667C43.1198 1.45579 34.0941 6.17981 31.7898 19.315" stroke="#0E3B4D" stroke-width="0.801887" stroke-linecap="round"/>
                  <path d="M31.8876 18.7839C34.3388 6.05213 43.1773 1.446 47.3446 0.735667C58.1532 -1.24075 63.3472 8.55809 65.5001 21.0191C65.5529 21.3246 65.3048 21.5992 64.9954 21.578L32.3235 19.3514C32.0383 19.332 31.8335 19.0646 31.8876 18.7839Z" fill="#0E3B4D"/>
                  <path d="M31.8386 55.7869L48.3524 38.248" stroke="#0E3B4D" stroke-width="0.801887" stroke-linecap="round"/>
                  <path d="M25.5012 19.6367L48.3519 38.2484L56.2465 44.6944C56.4284 44.8429 56.6908 44.8384 56.8675 44.6838L66.4045 36.3437" stroke="#0E3B4D" stroke-width="0.801887" stroke-linecap="round"/>
                  <path d="M25.6241 19.345L64.488 21.8889C64.663 21.9003 64.8162 22.0002 64.8859 22.1611C67.6589 28.5658 66.8842 45.2234 66.107 53.23C66.0844 53.4632 65.8971 53.6388 65.6634 53.6552L31.9762 56.0184C31.7772 56.0323 31.5929 55.9286 31.5104 55.747C28.6796 49.5119 23.6832 34.1586 25.1167 19.7729C25.1422 19.5166 25.3672 19.3282 25.6241 19.345Z" stroke="#0E3B4D" stroke-width="0.801887" stroke-linecap="round"/>
                  <path d="M26.6049 40.6308C21.0231 40.6308 20.7115 40.2185 14.9242 39.2593C14.6869 39.2198 14.4556 39.3608 14.3756 39.5877C12.6818 44.3915 5.90528 52.9635 1.97635 45.9598C0.872154 43.9915 -0.241645 38.3265 4.13674 31.4133C8.46093 24.5855 12.7664 27.779 14.4447 30.3102C14.4864 30.3731 14.5107 30.443 14.5177 30.5181C14.7155 32.6398 14.4884 36.2618 12.8929 39.1906C11.9973 40.8347 10.6705 42.2602 8.74565 43.0793C3.33022 45.3838 3.27267 39.1906 5.00098 35.878C5.86511 34.0056 7.44941 32.2774 9.75383 34.0056C11.6262 35.734 10.0419 37.6064 9.0337 38.9026" stroke="#0E3B4D" stroke-width="0.801887" stroke-linecap="round"/>
                  <path d="M6.00928 48.5862L29.3414 50.8563" stroke="#0E3B4D" stroke-width="0.801887" stroke-linecap="round"/>
                  <path d="M25.0204 30.4047C19.6914 30.4047 19.8354 30.4047 11.0498 27.5242" stroke="#0E3B4D" stroke-width="0.801887" stroke-linecap="round"/>
                  <path d="M9.03394 38.9021L12.8931 39.1901" stroke="#0E3B4D" stroke-width="0.801887" stroke-linecap="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_529_32998">
                    <rect width="119" height="81.1509" fill="white" transform="translate(0.5)"/>
                  </clipPath>
                </defs>
              </svg>

              <h4 class="m-t-3" v-if="selectedProfile">Message sent to {{ selectedProfile.brand_name }}</h4>
              <p class="p-t-2 m-0">If you decide to work together, share your Syncio store keys and connect via the <strong>Connect New Store</strong> button on the <strong>Stores page</strong>.</p>
              <p class="p-y-2 m-0">You'll be able to share inventory as soon as the connection is made.</p>
            </div>

            <div class="actions d-flex justify-end">
              <v-btn elevation="0" class="btn btn-border btn-message" @click="hideMessageDialogHandler">Close</v-btn>
            </div>
          </div>
        </v-card>
      </v-overlay>

      <v-overlay :value="showCreateProfileDialog" class="dialog-inner message-dialog">
        <v-card light width="506px">
          <div class="dialog-body">
            <div class="text-center">
              <svg width="81" height="80" viewBox="0 0 81 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M40.5 40.8335C38.0277 40.8335 35.611 40.1004 33.5554 38.7269C31.4998 37.3534 29.8976 35.4011 28.9515 33.117C28.0054 30.833 27.7579 28.3196 28.2402 25.8949C28.7225 23.4701 29.913 21.2428 31.6612 19.4947C33.4093 17.7465 35.6366 16.556 38.0614 16.0737C40.4861 15.5914 42.9995 15.8389 45.2835 16.785C47.5676 17.7311 49.5199 19.3333 50.8934 21.3889C52.2669 23.4445 53 25.8612 53 28.3335C53 31.6487 51.683 34.8281 49.3388 37.1723C46.9946 39.5165 43.8152 40.8335 40.5 40.8335ZM40.5 20.8335C39.0166 20.8335 37.5666 21.2734 36.3332 22.0975C35.0999 22.9216 34.1386 24.0929 33.5709 25.4634C33.0033 26.8338 32.8547 28.3418 33.1441 29.7967C33.4335 31.2515 34.1478 32.5879 35.1967 33.6368C36.2456 34.6857 37.582 35.4 39.0368 35.6894C40.4917 35.9788 41.9997 35.8303 43.3701 35.2626C44.7406 34.6949 45.9119 33.7336 46.736 32.5003C47.5601 31.2669 48 29.8169 48 28.3335C48 26.3444 47.2098 24.4367 45.8033 23.0302C44.3968 21.6237 42.4891 20.8335 40.5 20.8335V20.8335Z" fill="#0E3B4D"/>
                <path d="M63.8332 64.1668C63.1728 64.1582 62.5419 63.892 62.075 63.4251C61.608 62.9581 61.3418 62.3272 61.3332 61.6668C61.3332 55.1668 57.7998 50.8335 40.4998 50.8335C23.1998 50.8335 19.6665 55.1668 19.6665 61.6668C19.6665 62.3299 19.4031 62.9658 18.9343 63.4346C18.4654 63.9034 17.8295 64.1668 17.1665 64.1668C16.5035 64.1668 15.8676 63.9034 15.3987 63.4346C14.9299 62.9658 14.6665 62.3299 14.6665 61.6668C14.6665 45.8335 32.7665 45.8335 40.4998 45.8335C48.2332 45.8335 66.3332 45.8335 66.3332 61.6668C66.3245 62.3272 66.0584 62.9581 65.5914 63.4251C65.1244 63.892 64.4935 64.1582 63.8332 64.1668Z" fill="#212121"/>
              </svg>
              <h4 class="m-t-1">Create your profile to send a message</h4>
              <p class="p-t-2" style="font-size: 16px;">You're just one step away from sending a message to a <br> potential partner on Syncio 🙌</p>
              <p class="p-t-2" style="font-size: 16px;">It takes less than 5 minutes to review and create your <br> marketplace profile.</p>
            </div>

            <div class="actions d-flex justify-between p-t-4">
              <v-btn elevation="0" class="btn btn-border btn-message" @click="showCreateProfileDialog = false" style="width: auto;">Cancel</v-btn>
              <v-btn elevation="0" class="btn btn-border inverse btn-message" @click="goToReviewProfileHandler" style="width: auto;">Review profile</v-btn>
            </div>
          </div>
        </v-card>
      </v-overlay>
    </template>

    <template v-else>
      <section class="marketplace">
        <div class="marketplace-banner text-center">
          <h1>Awesome products and long lasting <br> partnerships, all in one place</h1>
          <p>Syncio Marketplace is coming soon. Connect with like-<br>minded stores to share inventory and boost sales.</p>
          <p>Review and set up your profile before the marketplace <br> launches 🚀</p>
          <router-link to="/settings/marketplace-settings" class="btn btn-learn-more d-inline-block">Review profile</router-link>
        </div>

        <h2 class="m-t-10 text-center">How it works</h2>

        <ul class="p-0 m-0 d-flex justify-content-between marketplace-steps">
          <li>
            <svg width="61" height="60" viewBox="0 0 61 60" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_283_12799)">
                <path d="M39.25 35H37.275L36.575 34.325C39.025 31.475 40.5 27.775 40.5 23.75C40.5 14.775 33.225 7.5 24.25 7.5C15.275 7.5 8 14.775 8 23.75C8 32.725 15.275 40 24.25 40C28.275 40 31.975 38.525 34.825 36.075L35.5 36.775V38.75L48 51.225L51.725 47.5L39.25 35V35ZM24.25 35C18.025 35 13 29.975 13 23.75C13 17.525 18.025 12.5 24.25 12.5C30.475 12.5 35.5 17.525 35.5 23.75C35.5 29.975 30.475 35 24.25 35Z" fill="#0E3B4D"/>
              </g>
              <defs>
                <clipPath id="clip0_283_12799">
                  <rect width="60" height="60" fill="white" transform="translate(0.5)"/>
                </clipPath>
              </defs>
            </svg>
            <span>Search and browse for partners</span>
          </li>
          <li>
            <svg width="61" height="60" viewBox="0 0 61 60" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_1225_36444)">
                <path d="M10.5 10H50.5V40H13.425L10.5 42.925V10ZM10.5 5C7.75 5 5.525 7.25 5.525 10L5.5 55L15.5 45H50.5C53.25 45 55.5 42.75 55.5 40V10C55.5 7.25 53.25 5 50.5 5H10.5ZM15.5 30H45.5V35H15.5V30ZM15.5 22.5H45.5V27.5H15.5V22.5ZM15.5 15H45.5V20H15.5V15Z" fill="#0E3B4D"/>
              </g>
              <defs>
                <clipPath id="clip0_1225_36444">
                  <rect width="60" height="60" fill="white" transform="translate(0.5)"/>
                </clipPath>
              </defs>
            </svg>
            <span>Message the partner store and agree to terms </span>
          </li>
          <li>
            <svg width="61" height="60" viewBox="0 0 61 60" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_283_12806)">
                <path d="M43 17.5H33V22.5H43C47.125 22.5 50.5 25.875 50.5 30C50.5 34.125 47.125 37.5 43 37.5H33V42.5H43C49.9 42.5 55.5 36.9 55.5 30C55.5 23.1 49.9 17.5 43 17.5ZM28 37.5H18C13.875 37.5 10.5 34.125 10.5 30C10.5 25.875 13.875 22.5 18 22.5H28V17.5H18C11.1 17.5 5.5 23.1 5.5 30C5.5 36.9 11.1 42.5 18 42.5H28V37.5ZM20.5 27.5H40.5V32.5H20.5V27.5Z" fill="#0E3B4D"/>
              </g>
              <defs>
                <clipPath id="clip0_283_12806">
                  <rect width="60" height="60" fill="white" transform="translate(0.5)"/>
                </clipPath>
              </defs>
            </svg>
            <span>Seamlessly connect using your unique store key</span>
          </li>
          <li>
            <svg width="61" height="60" viewBox="0 0 61 60" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_283_12813)">
                <path d="M30.5 5C16.7 5 5.5 16.2 5.5 30C5.5 43.8 16.7 55 30.5 55C44.3 55 55.5 43.8 55.5 30C55.5 16.2 44.3 5 30.5 5ZM30.5 50C19.475 50 10.5 41.025 10.5 30C10.5 18.975 19.475 10 30.5 10C41.525 10 50.5 18.975 50.5 30C50.5 41.025 41.525 50 30.5 50ZM31.275 27.85C26.85 26.725 25.425 25.5 25.425 23.675C25.425 21.575 27.4 20.1 30.675 20.1C34.125 20.1 35.425 21.75 35.525 24.2H39.8C39.675 20.85 37.625 17.775 33.575 16.775V12.5H27.75V16.725C23.975 17.525 20.95 19.975 20.95 23.75C20.95 28.225 24.675 30.475 30.1 31.775C34.975 32.925 35.95 34.65 35.95 36.45C35.95 37.775 34.975 39.925 30.7 39.925C26.7 39.925 25.125 38.125 24.9 35.825H20.6C20.85 40.075 24 42.475 27.75 43.25V47.5H33.6V43.325C37.4 42.6 40.4 40.425 40.425 36.4C40.4 30.9 35.675 29 31.275 27.85V27.85Z" fill="#0E3B4D"/>
              </g>
              <defs>
              <clipPath id="clip0_283_12813">
                <rect width="60" height="60" fill="white" transform="translate(0.5)"/>
              </clipPath>
              </defs>
            </svg>
            <span>Share inventory and grow together</span>
          </li>
        </ul>
      </section>
    </template>
  </article>
</template>

<style lang="scss" scoped>
.marketplace {
  .paging {
    background: none;
    border: none;
    padding: 0;
  }
}

.marketplace-profile-loader {
  .loader-image {
    background: rgba(233, 233, 233, 1);
    border-radius: 8px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    height: 224px;
    width: 100%;
  }

  .loader-bottom {
    background: #fff;
    padding: 20px;
  }

  .first, .second, .third {
    background: rgba(233, 233, 233, 1);
  }

  .first {
    height: 28px;
    width: 100%;
  }

  .second {
    width: 60%;
  }

  .third {
    width: 40%;
  }

  .second, .third {
    height: 24px;
  }
}

@-webkit-keyframes fadeinout {
  0%,100% { opacity: .55; }
  50% { opacity: 1; }
}

@keyframes fadeinout {
  0%,100% { opacity: .55; }
  50% { opacity: 1; }
}

.p-loading {
  animation: fadeinout .75s linear infinite;
}

.message-dialog {
  .v-card {
    border-radius: 12px;
    box-shadow: none !important;
  }

  .dialog-body {
    background: #fff;
    padding: 20px;
    border-radius: 12px !important;
    border: 1px solid rgba(0, 0, 0, 1);
  }

  h3 {
    font-size: 17.5px;
  }

  .message-box {
    border-radius: 6px;
    border: 1px solid rgba(206, 212, 218, 1);
    height: 127px;
    overflow-y: auto;
    padding: 10px;
    resize: none;
    width: 100%;

    &:is(:focus, :hover) {
      outline: none;
    }
  }

  .actions {
    margin-top: 12px;
  }

  .btn-message {
    border-radius: 6px;
    font-size: 14px !important;
    height: 38px !important;
    margin-top: 4px;
    width: 83px;
  }
}
</style>
